import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { Box, Stack } from '@mui/material';
import PageLayout from '../components/global/PageLayout';
import PageLoading from '../components/global/PageLoading';
import FlashImageUploadField from '../components/flashbook/FlashDesignForm/FlashImageUploadField';
import TagInputField from '../components/flashbook/FlashDesignForm/TagInputField';
import SizeConfigTable from '../components/flashbook/FlashDesignForm/SizeConfigTable';
import PaymentRulesSelector from '../components/flashbook/FlashDesignForm/PaymentRulesSelector';
import FormSection from '../components/flashbook/FlashDesignForm/FormSection';
import FlashFormHeader from '../components/flashbook/FlashDesignForm/FlashFormHeader';
import StorefrontPreviewCard from '../components/flashbook/FlashDesignForm/StorefrontPreviewCard';
import ScrollToFirstFormikError from '../components/form/ScrollToFirstFormikError';
import TextInput from '../components/form/base/TextInput';
import TextareaInput from '../components/form/base/TextareaInput';
import SwitchInput from '../components/form/base/SwitchInput';
import MultiSelect from '../components/form/base/MultiSelect';
import {
  CreateFlashDesignPayload,
  FlashDesignWithRelations,
  flashDesignFormSchema,
  FlashDesignFormPayload,
  FlashDesignPricingPayload,
  SizeConfig,
} from '../types/flashbooks';
import { FLASH_DESIGN_SIZE_OPTIONS } from '../constants/flashbooks';
import {
  getRefImagesFromFlashDesign,
  userHasAccessToShopFlash,
} from '../helpers/flashbooks';
import { getArtistOption } from '../services/business';
import useUser from '../hooks/global/useUser';
import useLoadingModal from '../hooks/global/useLoadingModal';
import useBusiness from '../hooks/businesses/useBusiness';
import useGetBusinessFlashbook from '../hooks/flashbooks/useGetBusinessFlashbook';
import useGetFlashbookDesign from '../hooks/flashbooks/useGetFlashbookDesign';
import useCreateFlashbookDesign from '../hooks/flashbooks/useCreateFlashbookDesign';
import useUpdateFlashbookDesign from '../hooks/flashbooks/useUpdateFlashbookDesign';
import { uploadFiles } from '../services/global';
import { ROUTES } from '../constants/global';

const buildEmptySizeConfigs = (): SizeConfig[] =>
  FLASH_DESIGN_SIZE_OPTIONS.map(({ value }) => ({
    size: value,
    enabled: false,
    durationInMinutes: null,
    estimate: '',
  }));

const parseAmount = (value: string) =>
  parseFloat(value.replace(/[^0-9.]/g, '') || '0');

const sizeConfigsToPricing = (
  configs: FlashDesignFormPayload['sizeConfigs'],
): FlashDesignPricingPayload[] =>
  configs.map(config => ({
    enabled: config.enabled,
    size: config.size,
    price: config.enabled ? parseAmount(config.estimate ?? '') : 0,
    estimatedDurationInMinutes:
      config.enabled && config.durationInMinutes
        ? Math.max(0, Math.round(Number(config.durationInMinutes)) || 0)
        : 0,
  }));

const designToFormValues = (
  design: FlashDesignWithRelations,
  allArtistIds: string[],
): FlashDesignFormPayload => {
  const [coverImage] = getRefImagesFromFlashDesign(design);
  const sizeConfigs: SizeConfig[] = FLASH_DESIGN_SIZE_OPTIONS.map(
    ({ value }) => {
      const pricing = design.pricing.find(p => p.size === value);
      return {
        size: value,
        enabled: !!pricing?.enabled,
        durationInMinutes: pricing?.estimatedDurationInMinutes ?? null,
        estimate: pricing && pricing.price > 0 ? String(pricing.price) : '',
      };
    },
  );

  return {
    name: design.name,
    description: design.description ?? '',
    imageUrl: coverImage ?? '',
    tags: design.tags ?? [],
    isRepeatable: design.isRepeatable,
    enableDirectBookings: design.enableDirectBookings,
    depositBehavior: design.depositBehavior,
    depositAmount:
      design.depositBehavior === 'FLAT_FEE' && design.depositAmount > 0
        ? String(design.depositAmount)
        : '',
    depositPercentage:
      design.depositBehavior === 'PERCENTAGE' && design.depositPercentage > 0
        ? String(design.depositPercentage * 100)
        : '',
    sizeConfigs,
    bookableArtistIds: design.allBookableArtists
      ? allArtistIds
      : design.assignedEmployees
          .map(artist => artist.employeeId)
          .filter(id => allArtistIds.includes(id)),
  };
};

export default function FlashDesignFormPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const { flashDesignId } = useParams<{ flashDesignId?: string }>();
  const isEdit = !!flashDesignId;

  const { business } = useBusiness();
  const { data: flashbook, isLoading: isLoadingFlashbook } =
    useGetBusinessFlashbook();
  const numericFlashDesignId = flashDesignId
    ? Number(flashDesignId)
    : undefined;
  const { data: existingDesign, isLoading: isLoadingDesign } =
    useGetFlashbookDesign({
      flashbookId: flashbook?.id,
      flashDesignId: numericFlashDesignId,
      enabled: isEdit,
    });
  const [imageFile, setImageFile] = useState<File | null>(null);

  const { mutateAsync: createDesign } = useCreateFlashbookDesign();
  const { mutateAsync: updateDesign } = useUpdateFlashbookDesign();

  const artistOptions = useMemo(
    () =>
      user
        ? (user.business.employees ?? [])
            .filter(etb => etb.isBookable)
            .map(etb => getArtistOption(etb, user))
        : [],
    [user],
  );

  const initialValues = useMemo<FlashDesignFormPayload>(() => {
    const allArtistIds = artistOptions.map(option => option.value);
    if (existingDesign) return designToFormValues(existingDesign, allArtistIds);
    return {
      name: '',
      description: '',
      imageUrl: '',
      tags: [],
      isRepeatable: true,
      enableDirectBookings: flashbook?.enableDirectBookings ?? true,
      depositBehavior: flashbook?.depositBehavior ?? 'NONE',
      depositAmount:
        flashbook && flashbook.depositAmount > 0
          ? String(flashbook.depositAmount)
          : '',
      depositPercentage:
        flashbook && flashbook.depositPercentage > 0
          ? String(flashbook.depositPercentage * 100)
          : '',
      sizeConfigs: buildEmptySizeConfigs(),
      bookableArtistIds: allArtistIds,
    };
  }, [flashbook, existingDesign, artistOptions]);

  const onCancel = () => navigate(ROUTES.flashbook);

  const onSubmit = async (values: FlashDesignFormPayload) => {
    if (!flashbook?.id || !business?.id) return;

    const optionIds = artistOptions.map(option => option.value);
    const selectedArtistIds = values.bookableArtistIds.filter(id =>
      optionIds.includes(id),
    );
    const allBookableArtists = optionIds.every(id =>
      selectedArtistIds.includes(id),
    );

    if (!allBookableArtists && selectedArtistIds.length === 0) {
      toast.error('Select at least one artist, or choose All artists');
      return;
    }

    openLoadingModal({
      header: isEdit ? 'Saving flash design' : 'Creating flash design',
      message: 'Please wait while we save your flash design...',
    });

    try {
      let { imageUrl } = values;
      if (imageFile) {
        const path = `tempImages/flashbook-design-${business.id}-${Date.now()}`;
        const [uploadedUrl] = await uploadFiles([
          { id: imageFile.name, path, url: imageUrl },
        ]);
        if (!uploadedUrl) throw new Error('Image upload failed');
        imageUrl = uploadedUrl;
      }

      const sharedPayload = {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        tags: values.tags,
        isRepeatable: values.isRepeatable,
        enableDirectBookings: values.enableDirectBookings,
        depositBehavior: values.depositBehavior,
        depositAmount:
          values.depositBehavior === 'FLAT_FEE'
            ? parseAmount(values.depositAmount ?? '')
            : 0,
        depositPercentage:
          values.depositBehavior === 'PERCENTAGE'
            ? parseAmount(values.depositPercentage ?? '') / 100
            : 0,
        images: [imageUrl],
        pricing: sizeConfigsToPricing(values.sizeConfigs),
        allBookableArtists,
        assignedEmployeeIds: allBookableArtists ? undefined : selectedArtistIds,
      };

      if (isEdit && numericFlashDesignId) {
        await updateDesign({
          flashbookId: flashbook.id,
          flashDesignId: numericFlashDesignId,
          payload: sharedPayload,
        });
      } else {
        await createDesign({
          flashbookId: flashbook.id,
          payload: sharedPayload as CreateFlashDesignPayload,
        });
      }

      toast.success('Flash design saved');
      navigate(ROUTES.flashbook);
    } catch (error) {
      toast.error('Failed to save flash design');
    } finally {
      closeLoadingModal();
    }
  };

  const designName = existingDesign?.name;
  const crumbLabel = isEdit
    ? `Edit · ${designName ?? 'Flash'}`
    : 'Add new flash';
  const titleText = isEdit
    ? `Edit flash${designName ? ` · ${designName}` : ''}`
    : 'Add new flash';
  const subtitle = isEdit
    ? 'Update the design, booking rules, sizing, or payment rules.'
    : 'Upload a design and set booking details so clients can reserve it from your storefront.';
  const saveLabel = isEdit ? 'Save changes' : 'Add flash';

  return (
    <PageLayout
      headerIsHidden
      guard={{
        isAllowed: !!user && userHasAccessToShopFlash(user),
      }}>
      {isLoadingFlashbook || (isEdit && isLoadingDesign) ? (
        <PageLoading />
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={flashDesignFormSchema}
          onSubmit={onSubmit}>
          {({ isSubmitting }) => (
            <Form style={{ width: '100%', padding: 0, gap: 0 }}>
              <ScrollToFirstFormikError />
              <FlashFormHeader
                crumbLabel={crumbLabel}
                title={titleText}
                subtitle={subtitle}
                saveLabel={saveLabel}
                onCancel={onCancel}
                isSubmitting={isSubmitting}
              />
              <Box
                width="100%"
                display="grid"
                gridTemplateColumns={{ xs: '1fr', lg: '1fr 420px' }}
                gap={5}
                alignItems="start">
                <Box minWidth={0}>
                  <FormSection
                    title="Flash design overview"
                    hint="A single photo or scan of your flash design. Square works best — it'll crop to square on your storefront.">
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      gap={2.5}
                      alignItems="flex-start">
                      <FlashImageUploadField onPickFile={setImageFile} />
                      <Stack flex={1} gap={2} width="100%">
                        <TextInput
                          name="name"
                          label="Title"
                          placeholder="e.g. Rose & Dagger"
                          required
                        />
                        <TextareaInput
                          name="description"
                          label="Description (optional)"
                          placeholder="Share a quick story, style notes, or placement suggestions."
                          rows={3}
                        />
                        <TagInputField flashbookId={flashbook?.id} />
                      </Stack>
                    </Stack>
                  </FormSection>
                  <FormSection
                    title="Booking rules"
                    hint="Control how clients can book this flash.">
                    <Stack
                      sx={{
                        '& > *': {
                          py: 1.75,
                          borderTop: '1px solid',
                          borderColor: 'darkerLightGrey.main',
                        },
                        '& > *:first-of-type': {
                          pt: 0,
                          borderTop: 0,
                        },
                        '& > *:last-of-type': {
                          pb: 0,
                        },
                      }}>
                      <SwitchInput
                        name="isRepeatable"
                        label="Repeatable design"
                        description="Toggle on to allow multiple bookings for this same flash."
                      />
                      <SwitchInput
                        name="enableDirectBookings"
                        label="Enable direct bookings"
                        description="Allow clients to schedule this flash directly without approval."
                      />
                      <MultiSelect
                        name="bookableArtistIds"
                        label="Bookable artists"
                        allText="All artists"
                        placeholder="Select artists"
                        options={artistOptions}
                        required
                      />
                    </Stack>
                  </FormSection>
                  <FormSection
                    title="Booking configuration"
                    hint="Choose sizes this design is available at, and set duration + estimate for each.">
                    <SizeConfigTable />
                  </FormSection>
                  <FormSection
                    title="Payment rules"
                    hint="Choose how payment is collected when a client books this flash. Your default flash settings are used if nothing is selected."
                    isLast>
                    <PaymentRulesSelector />
                  </FormSection>
                </Box>
                <StorefrontPreviewCard />
              </Box>
            </Form>
          )}
        </Formik>
      )}
    </PageLayout>
  );
}
